import { UploadOutlined, MobileOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Button, Card, Col, Divider, Form, Layout, Progress, Row, Select, Typography, Upload, List, Avatar } from 'antd';
import { Content } from 'antd/es/layout/layout';
import React, { useEffect, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { v4 as uuidv4 } from 'uuid';
import { io, Socket } from 'socket.io-client';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL_ADMIN } from '../../config/config';
import { mapProjectionList } from '~/common/typescript/services/map_projection/MapProjection';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const SOCKET_URL = API_URL_ADMIN;

const generatePin = () => Math.floor(10000 + Math.random() * 90000).toString();

const TransferToMobileScreen: React.FC = () => {
    const [roomId, setRoomId] = useState<string>('');
    const [pin, setPin] = useState<string>('');
    const [connectedDevices, setConnectedDevices] = useState<any[]>([]);
    const [fileList, setFileList] = useState<any[]>([]);
    const [uploading, setUploading] = useState(false);
    const [socket, setSocket] = useState<Socket | null>(null);
    const [importProgress, setImportProgress] = useState<{ title: string; progress?: number } | null>(null);
    const [form] = Form.useForm();

    useEffect(() => {
        const newRoomId = uuidv4();
        const newPin = generatePin();
        setRoomId(newRoomId);
        setPin(newPin);

        const newSocket = io(SOCKET_URL);

        newSocket.on('connect', () => {
            console.log('Connected to socket server');
            newSocket.emit('join_room', newRoomId);
            newSocket.emit('register_pin', { pin: newPin, roomId: newRoomId });
        });

        newSocket.on('mobile_connected', (deviceInfo) => {
            console.log('Mobile connected:', deviceInfo);
            toast.success(`Thiết bị ${deviceInfo.name || 'Mobile'} đã kết nối!`);
            setConnectedDevices((prev) => {
                if (prev.find(d => d.id === deviceInfo.id)) return prev;
                return [...prev, deviceInfo];
            });
        });

        newSocket.on('import_progress', ({ title, progress }: { title: string; progress?: number }) => {
            setImportProgress({ title, progress });
        });

        newSocket.on('import_done', () => {
            setImportProgress(null);
            toast.success('Thiết bị đã nhập dữ liệu xong!');
        });

        setSocket(newSocket);

        return () => {
            newSocket.disconnect();
        };
    }, []);

    const handleUpload = async () => {
        if (connectedDevices.length === 0) {
            toast.error('Vui lòng kết nối ít nhất 1 thiết bị di động.');
            return;
        }
        if (fileList.length === 0) {
            toast.error('Vui lòng chọn tệp tin.');
            return;
        }

        const formData = new FormData();
        fileList.forEach((file) => {
            formData.append('files', file as any);
        });

        setUploading(true);

        try {
            // Upload all files to Strapi
            const response = await axios.post(`${SOCKET_URL}/api/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            const uploadedFiles = response.data;
            if (uploadedFiles && uploadedFiles.length > 0) {
                // Build array of { fileUrl, fileName } for all uploaded files
                const files = uploadedFiles.map((f: any) => ({
                    fileUrl: `${SOCKET_URL}${f.url}`,
                    fileName: f.name,
                    fileId: f.id,
                }));

                // Get settings from form
                const settings = form.getFieldsValue();

                // Emit all files to mobile
                socket?.emit('file_prepared', { roomId, files, settings });

                const names = files.map((f: any) => f.fileName).join(', ');
                toast.success(`Đã gửi ${files.length} tệp (${names}) tới ${connectedDevices.length} thiết bị!`);
                setFileList([]);
            }
        } catch (error) {
            console.error('Upload failed:', error);
            toast.error('Tải lên thất bại.');
        } finally {
            setUploading(false);
        }
    };

    const props = {
        onRemove: (file: any) => {
            setFileList((curr) => {
                const index = curr.indexOf(file);
                const newFileList = curr.slice();
                newFileList.splice(index, 1);
                return newFileList;
            });
        },
        beforeUpload: (file: any) => {
            setFileList((curr) => [...curr, file]);
            return false;
        },
        fileList,
    };

    return (
        <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
            <Content style={{ padding: '24px' }}>
                <Row gutter={[24, 24]} justify="center">
                    <Col span={24}>
                        <Title level={2}>Truyền tệp sang Geocollect</Title>
                    </Col>
                    <Col xs={24} md={12} lg={8}>
                        <Card title="1. Quét mã QR bằng App" bordered={false} style={{ height: '100%' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
                                <div style={{ background: 'white', padding: 16, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                    {roomId && <QRCodeCanvas value={JSON.stringify({ type: 'import_map', roomId, url: SOCKET_URL })} size={250} />}
                                </div>
                                <Divider plain style={{ margin: '20px 0 12px' }}>hoặc nhập mã PIN</Divider>
                                <div style={{ background: '#f6ffed', border: '2px solid #b7eb8f', borderRadius: 12, padding: '12px 32px', textAlign: 'center' }}>
                                    <Text style={{ fontSize: 36, fontWeight: 'bold', letterSpacing: 10, fontFamily: 'monospace', color: '#389e0d' }}>
                                        {pin}
                                    </Text>
                                </div>
                                <Text type="secondary" style={{ marginTop: 8, fontSize: 12 }}>Mã PIN hết hạn sau 10 phút</Text>
                                <Paragraph style={{ marginTop: 16, textAlign: 'center' }}>
                                    Mở ứng dụng <strong>Geocollect</strong> trên điện thoại,<br /> chọn <strong>Nhập dữ liệu</strong> &gt; <strong>Thông qua Geocloud</strong>
                                </Paragraph>
                            </div>
                        </Card>
                    </Col>

                    <Col xs={24} md={12} lg={12}>
                        <Card
                            title={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>2. Chọn tệp & Cấu hình</span>
                                    {connectedDevices.length > 0 && <span style={{ color: '#52c41a' }}><CheckCircleOutlined /> {connectedDevices.length} thiết bị đã kết nối</span>}
                                </div>
                            }
                            bordered={false}
                            style={{ height: '100%' }}
                        >
                            <List
                                itemLayout="horizontal"
                                dataSource={connectedDevices}
                                renderItem={(item) => (
                                    <List.Item>
                                        <List.Item.Meta
                                            avatar={<Avatar icon={<MobileOutlined />} style={{ backgroundColor: '#87d068' }} />}
                                            title={item.name}
                                            description={item.id}
                                        />
                                    </List.Item>
                                )}
                            />
                            {importProgress && (
                                <div style={{ marginBottom: 16 }}>
                                    <div style={{ marginBottom: 4, color: '#595959' }}>{importProgress.title}</div>
                                    <Progress
                                        percent={importProgress.progress != null ? Math.round(importProgress.progress * 100) : 100}
                                        status="active"
                                        strokeColor="#1677ff"
                                    />
                                </div>
                            )}

                            <Divider />

                            <Form layout="vertical" form={form} initialValues={{ encoding: 'utf-8', projection: 'auto' }}>
                                <Form.Item label="Chọn tệp tin (KML, GeoJSON, MBTiles, Shapefile, MapInfo, ...)  — có thể chọn nhiều tệp">
                                    <Upload {...props} multiple>
                                        <Button icon={<UploadOutlined />}>Chọn tệp tin</Button>
                                    </Upload>
                                </Form.Item>

                                <Form.Item name="projection" label="Hệ chiếu">
                                    <Select showSearch optionFilterProp="label">
                                        <Option key="auto" value="auto" label="Tự động phát hiện">
                                            Tự động phát hiện
                                        </Option>
                                        {mapProjectionList.map(p => (
                                            <Option key={p.id} value={p.srs_code} label={p.name}>
                                                {p.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                <Form.Item name="encoding" label="Bảng mã">
                                    <Select>
                                        <Option value="utf-8">UTF-8 (Unicode)</Option>
                                        <Option value="cp1258">TCVN3</Option>
                                    </Select>
                                </Form.Item>

                                <Form.Item>
                                    <Button
                                        type="primary"
                                        onClick={handleUpload}
                                        loading={uploading}
                                        disabled={fileList.length === 0 || connectedDevices.length === 0}
                                        block
                                        size="large"
                                        icon={<MobileOutlined />}
                                    >
                                        Gửi tới {connectedDevices.length} thiết bị
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Card>
                    </Col>
                </Row>
            </Content>
        </Layout>
    );
};

export default TransferToMobileScreen;
